/**
 * Stage 10 — Pit Optimization / Mine Design (strict gate).
 *
 * Embeds MinePlanTab. Both the base pit-shell form ("New") and the LG
 * Optimize form live inside it. Backend hooks:
 *   • POST /mine-plans           → Stage 10 + 11 complete (Session C)
 *   • POST /mine-plans/optimize  → Stage 10 + 11 complete (Session E-2)
 *   • POST /mine-plans/npv-sched → Stage 11 + 12 complete (Session E-2)
 *
 * NOTE (P1 gap surfaced honestly): the LG optimizer currently uses
 * NetworkX max-flow. A compiled pseudoflow solver is planned but has
 * NOT been implemented in this session or Session E-1.
 */
import { useEffect, useState } from "react";
import MinePlanTab from "@/components/tabs/MinePlanTab";
import PitDesignPanel from "@/components/pit/PitDesignPanel";
import AIAutopilotPanel from "../AIAutopilotPanel";
import SearchMultiplierSlider from "@/components/SearchMultiplierSlider";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";
import api from "@/lib/api";

export default function Stage10PitOptimization({ projectId, onCompleted }) {
  const [_smMode, _setSmMode] = useStageMode(projectId, 10, "ai");
  const [pollActive, setPollActive] = useState(false);
  const [searchMult, setSearchMult] = useState(2.0);
  const [pitBmId, setPitBmId] = useState(null);
  const [pitBms, setPitBms] = useState([]);
  useEffect(() => {
    if (!pollActive || !projectId) return undefined;
    const t = setInterval(() => onCompleted?.(), 3500);
    const stop = setTimeout(() => setPollActive(false), 60_000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [pollActive, projectId, onCompleted]);

  // Load block models so the Sprint-D pit design panel can reconcile
  // tonnage / strip ratio against a live estimation.
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/block-models`)
      .then((r) => {
        const list = r.data || [];
        setPitBms(list);
        if (list.length && !pitBmId) setPitBmId(list[0].id || list[0]._id);
      })
      .catch(() => setPitBms([]));
  }, [projectId]);

  return (
    <div data-testid="stage10-content" className="space-y-4">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-neutral-100">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">Input mode</div>
        <StageModeSwitcher mode={_smMode} onChange={_setSmMode} testidPrefix="stage10-mode" />
      </div>
      <AIAutopilotPanel
        projectId={projectId}
        title="AI Auto-Fill · Lerchs–Grossmann pit shell"
        description="Derives revenue-per-grade-unit, mining/processing costs and slope from the project commodity profile, then runs LG max-flow optimisation in one click. The drilled-envelope filter below constrains the pit to blocks with drillhole support."
        autoFillPath="auto-fill/pit-optimize"
        executePath={`/projects/${projectId}/mine-plans/optimize`}
        buildPayload={(f) => ({
          name: "AI LG Optimised Pit",
          slope_deg: f.slope_deg ?? 45.0,
          revenue_per_grade_unit: f.revenue_per_grade_unit ?? 200.0,
          mining_cost: f.mining_cost ?? 4.0,
          processing_cost: f.processing_cost ?? 15.0,
          schedule_periods: f.schedule_periods ?? 5,
          recovery_pct: f.recovery_pct ?? 90.0,
          search_multiplier: searchMult,
        })}
        successLabel="Pit optimised — Stage 7 complete"
        buttonLabel="Run LG with commodity defaults"
        onSuccess={() => { setPollActive(true); onCompleted?.(); }}
        testid="stage10-autopilot"
      />

      <SearchMultiplierSlider
        projectId={projectId}
        value={searchMult}
        onChange={setSearchMult}
      />
      <div className="border border-neutral-200 bg-neutral-50 rounded p-3 text-[12px] text-neutral-800">
        <span className="text-[#4CAF50] font-medium">Manual mode:</span>{" "}
        create a pit design via <strong>New</strong> or run <strong>LG Optimize</strong>{" "}
        below with your own inputs. Auto-completes Stage 7 on submit.
        <button
          onClick={() => { setPollActive(true); onCompleted?.(); }}
          className="ml-3 text-[#4CAF50] hover:text-[#2E7D32] underline text-[11.5px]"
          data-testid="stage10-refresh-state">
          Refresh state
        </button>
      </div>

      <div className="border border-neutral-200 rounded-md bg-neutral-50 overflow-hidden"
           data-testid="stage10-mineplan-wrapper">
        {/* Wrap in a fixed container so the tab's h-[calc(100vh-…)]
            layout doesn't overflow the stepper column. */}
        <div style={{ height: "calc(100vh - 22rem)", minHeight: 500 }}>
          <MinePlanTab projectId={projectId} onChange={() => onCompleted?.()} />
        </div>
      </div>

      {/* Sprint D · Feb 2026 — 3D benched pit design with live reconciliation. */}
      <div className="pt-2 space-y-2" data-testid="stage10-pit-design-wrapper">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">
              Sprint D · 3D Pit Design
            </div>
            <p className="text-[12px] text-neutral-700 max-w-2xl">
              Sculpt a benched open-pit shell from six engineering parameters and reconcile
              tonnage &amp; strip ratio against a saved block model in real time.
            </p>
          </div>
          {pitBms.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                Reconcile against
              </label>
              <select
                value={pitBmId || ""}
                onChange={(e) => setPitBmId(e.target.value || null)}
                data-testid="stage10-pit-bm-select"
                className="h-8 rounded-none border border-neutral-300 bg-white text-[11px] font-mono px-2"
              >
                <option value="">— No block model —</option>
                {pitBms.map((b) => (
                  <option key={b.id || b._id} value={b.id || b._id}>
                    {b.name} · {(b.summary?.total_blocks ?? 0).toLocaleString()} blocks
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <PitDesignPanel projectId={projectId} blockModelId={pitBmId} />
      </div>
    </div>
  );
}
